import React, { useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  FolderTree,
  LayoutDashboard,
  Building2,
  Brain,
  Layers,
  Star,
  ChevronRight,
} from 'lucide-react';

interface Section {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const sections: Section[] = [
  { id: 'overview', label: 'Overview', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'collections', label: 'Collections', icon: <Building2 className="h-4 w-4" /> },
  { id: 'library', label: 'Library', icon: <FolderTree className="h-4 w-4" /> },
  { id: 'cards', label: 'Vocab Cards', icon: <Layers className="h-4 w-4" /> },
  { id: 'practice', label: 'Practice Sessions', icon: <Brain className="h-4 w-4" /> },
  { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'ai', label: 'AI Features', icon: <Star className="h-4 w-4" /> },
];

const SectionTitle: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <h2 id={id} className="text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
    {children}
  </h2>
);

const SubTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">{children}</h3>
);

const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 my-4 text-sm text-blue-800">
    <span className="font-semibold">Tip: </span>{children}
  </div>
);

export const HelpPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="flex gap-8">
      {/* Sticky sidebar */}
      <aside className="hidden lg:block w-56 flex-shrink-0">
        <div className="sticky top-6 bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">On this page</p>
          <nav className="space-y-1">
            {sections.map(({ id, label, icon }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left ${
                  activeSection === id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {icon}
                {label}
                {activeSection === id && <ChevronRight className="h-3 w-3 ml-auto" />}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Content */}
      <article className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg p-8 space-y-12">

        {/* Overview */}
        <section>
          <SectionTitle id="overview">Welcome to VocaBranch</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            VocaBranch is a vocabulary learning app designed to help you build and retain English vocabulary
            through organized study and AI-assisted practice. You create cards for words you want to learn,
            organize them into a hierarchy, and reinforce them through interactive practice sessions
            that use spaced repetition to keep knowledge fresh.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            The core loop is simple: <strong>add a word → enrich it with AI → practice it</strong>.
            Over time, the app tracks your reviews and helps you focus on what needs attention.
          </p>
        </section>

        {/* Collections */}
        <section>
          <SectionTitle id="collections">Collections</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            A <strong>Collection</strong> is a self-contained vocabulary workspace. Everything you create —
            categories, folders, and cards — lives inside a collection. You can have multiple collections
            to keep different study projects separate (e.g. one for business vocabulary, another for a
            specific exam).
          </p>

          <SubTitle>Switching collections</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            When you have more than one collection, a selector appears in the top-right header. Click it to
            switch between collections. Your last-used collection is remembered automatically.
          </p>

          <SubTitle>Sharing a collection</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            From the <strong>Collections</strong> page you can invite other users by email. Invited users
            get viewer access — they can browse and practice cards but cannot edit the content.
          </p>

          <Tip>Your first collection is created automatically when you sign up.</Tip>
        </section>

        {/* Library */}
        <section>
          <SectionTitle id="library">Library</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            The <strong>Library</strong> is where you organize your vocabulary. It uses a three-level hierarchy:
          </p>

          <div className="mt-4 space-y-4">
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-semibold text-gray-800">Categories</p>
                <p className="text-sm text-gray-600 mt-1">
                  Top-level groupings for broad topics — for example "Phrasal Verbs", "Academic Vocabulary",
                  or "Travel". Think of them as tabs that divide your collection into major themes.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-semibold text-gray-800">Folders</p>
                <p className="text-sm text-gray-600 mt-1">
                  Sub-containers within a category (or inside another folder). Use folders to group related
                  words — e.g. a "Cooking" folder inside a "Daily Life" category.
                  Folders can be nested as deep as you need.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-semibold text-gray-800">Cards</p>
                <p className="text-sm text-gray-600 mt-1">
                  Individual vocabulary entries — one word or phrase per card.
                  Cards live inside a folder or directly under a category.
                </p>
              </div>
            </div>
          </div>

          <SubTitle>Organizing your library</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Use the <strong>+</strong> button next to any category or folder to add a new folder or card.
            You can also drag and drop items to rearrange them, or use the move option in the card/folder
            menu to relocate across categories. For bulk operations, toggle selection mode to move or
            delete multiple cards at once.
          </p>

          <Tip>Cards without a category appear in an "Uncategorized" section at the top of the Library.</Tip>
        </section>

        {/* Vocab Cards */}
        <section>
          <SectionTitle id="cards">Vocab Cards</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            Each card represents one vocabulary item and can hold a rich set of linguistic information.
            Cards can be filled manually or populated automatically using AI enrichment.
          </p>

          <SubTitle>Core fields</SubTitle>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden mt-2">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-gray-700 border-b border-gray-200">Field</th>
                  <th className="text-left px-4 py-2 font-semibold text-gray-700 border-b border-gray-200">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ['Meanings', 'One or more definitions, each tagged with a grammar role (noun, verb, adjective…) and example phrases.'],
                  ['IPA', 'International Phonetic Alphabet pronunciation guide.'],
                  ['Synonyms / Antonyms', 'Related words with similar or opposite meanings.'],
                  ['Collocations', 'Words that naturally appear alongside the target word.'],
                  ['Word Forms', 'Inflections — plural, past tense, adjective form, adverb form, etc.'],
                  ['Register', 'Formality level: formal, neutral, informal, slang, etc.'],
                  ['Etymology', 'The historical origin of the word.'],
                  ['Notes', 'Your personal notes about the word.'],
                ].map(([field, desc]) => (
                  <tr key={field} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">{field}</td>
                    <td className="px-4 py-2 text-gray-600">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SubTitle>Review metadata</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Every card automatically tracks its <strong>review count</strong>, <strong>last reviewed date</strong>,
            and <strong>next review date</strong>. These are updated each time you complete a practice session
            for the card and drive the spaced-repetition scheduling.
          </p>
        </section>

        {/* Practice Sessions */}
        <section>
          <SectionTitle id="practice">Practice Sessions</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            Practice sessions are the main way to reinforce vocabulary. Each session walks you through
            a series of AI-evaluated challenges that test different aspects of the word.
          </p>

          <SubTitle>Single-card practice</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Start from any card's detail view. The session has five steps:
          </p>
          <ol className="list-decimal list-inside space-y-2 mt-3 text-gray-600 text-sm">
            <li><strong>Definition</strong> — Write what the word means in your own words. AI checks accuracy.</li>
            <li><strong>Example phrase</strong> — Construct a natural sentence using the word. AI evaluates grammar, tone, and naturalness.</li>
            <li><strong>Synonyms</strong> — List words with similar meaning. AI validates each one.</li>
            <li><strong>Antonyms</strong> — List words with opposite meaning. AI validates each one.</li>
            <li><strong>Phrase practice</strong> — Free-form sentence practice with AI feedback on multiple dimensions.</li>
          </ol>

          <SubTitle>Folder &amp; category practice</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Practise all cards in a folder (or an entire category) at once. Cards are shuffled and
            presented one at a time. You can skip a card to push it to the back of the queue.
            A completion screen appears once you've worked through every card.
          </p>

          <SubTitle>Spaced repetition</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Each time you complete a practice session for a card, the backend updates its review count
            and schedules the next optimal review date. The Dashboard shows which cards are due for
            review so you can prioritise your study time.
          </p>

          <SubTitle>What counts as a review?</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            A review is recorded only when you <strong>complete an AI-evaluated step</strong> for a card.
            The actions that trigger a review are:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-3 text-sm text-gray-600">
            <li>Submitting your definition and receiving AI feedback</li>
            <li>Submitting an example phrase for AI evaluation</li>
            <li>Submitting synonyms or antonyms for AI validation</li>
            <li>Completing a phrase practice round</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            Actions that do <strong>not</strong> count as a review: opening a card, browsing the Library,
            or starting a practice session without submitting any answers.
          </p>
        </section>

        {/* Dashboard */}
        <section>
          <SectionTitle id="dashboard">Dashboard</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            The Dashboard is your study home screen. It gives you a quick picture of your activity and
            surfaces the most relevant cards to work on next.
          </p>

          <SubTitle>Review activity chart</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            A bar chart shows how many cards you reviewed each day over the last 7 or 30 days.
            Switch between windows using the toggle above the chart.
          </p>

          <SubTitle>Last reviewed cards</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            A sortable list of recently practiced cards showing review count, last review date,
            and the next scheduled review date. Use this to spot cards that are coming due.
          </p>

          <SubTitle>Recently created cards</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Cards you added in the last 7 or 30 days, with a direct link to start practicing each one.
            Filterable by collection if you have more than one.
          </p>
        </section>

        {/* AI Features */}
        <section>
          <SectionTitle id="ai">AI Features</SectionTitle>
          <p className="text-gray-600 leading-relaxed">
            VocaBranch uses AI at multiple points to reduce manual work and make practice more meaningful.
          </p>

          <SubTitle>AI card creation</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Instead of filling in every field yourself, you can enter just the word (and optionally a
            short prompt or context) and let AI generate meanings, examples, synonyms, antonyms,
            collocations, IPA, register, and etymology automatically.
          </p>

          <SubTitle>AI enrichment for existing cards</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            Already have a card with just a name? Use the AI enrich button on the card detail page to
            fill in missing information. You can also bulk-enrich up to 10 cards at once from the Library.
          </p>

          <SubTitle>AI-evaluated practice</SubTitle>
          <p className="text-gray-600 leading-relaxed">
            During practice sessions, AI evaluates your answers in real time — checking whether your
            definition is accurate, whether your example sentence is grammatically correct and natural,
            and whether your synonyms/antonyms actually match the word. You get instant feedback with
            explanations and suggested alternatives.
          </p>

          <Tip>
            AI enrichment works best when the word already exists in the AI's training data. For very
            niche or invented terms, review AI-generated content before using it for study.
          </Tip>
        </section>

      </article>
    </div>
  );
};
